import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiService } from '@/services/api'
import { Group, GroupMember } from '@/types'

interface GroupsState {
  list: Group[]
  membersById: Record<number, GroupMember[]>
  isLoading: boolean
  membersLoading: Record<number, boolean>
  error: string | null
}

const initialState: GroupsState = {
  list: [],
  membersById: {},
  isLoading: false,
  membersLoading: {},
  error: null,
}

export const fetchGroups = createAsyncThunk('groups/fetchGroups', async () => {
  return await apiService.getGroups()
})

export const createGroup = createAsyncThunk(
  'groups/createGroup',
  async (data: { name: string }) => {
    return await apiService.createGroup(data)
  }
)

export const updateGroup = createAsyncThunk(
  'groups/updateGroup',
  async ({ id, name }: { id: number; name: string }) => {
    return await apiService.updateGroup(id, { name })
  }
)

export const deleteGroup = createAsyncThunk('groups/deleteGroup', async (id: number) => {
  await apiService.deleteGroup(id)
  return id
})

export const fetchGroupMembers = createAsyncThunk(
  'groups/fetchGroupMembers',
  async (groupId: number) => {
    const members = await apiService.getGroupMembers(groupId)
    return { groupId, members }
  }
)

export const addGroupMember = createAsyncThunk(
  'groups/addGroupMember',
  async ({ groupId, userId }: { groupId: number; userId: number }) => {
    const member = await apiService.addGroupMember(groupId, { user_id: userId })
    return { groupId, member }
  }
)

export const removeGroupMember = createAsyncThunk(
  'groups/removeGroupMember',
  async ({ groupId, userId }: { groupId: number; userId: number }) => {
    await apiService.removeGroupMember(groupId, userId)
    return { groupId, userId }
  }
)

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.isLoading = false
        state.list = action.payload
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to load groups'
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.list.push(action.payload)
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        const idx = state.list.findIndex((g) => g.id === action.payload.id)
        if (idx >= 0) state.list[idx] = action.payload
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.list = state.list.filter((g) => g.id !== action.payload)
      })
      .addCase(fetchGroupMembers.pending, (state, action) => {
        state.membersLoading[action.meta.arg] = true
      })
      .addCase(fetchGroupMembers.fulfilled, (state, action) => {
        const { groupId, members } = action.payload
        state.membersLoading[groupId] = false
        state.membersById[groupId] = members
      })
      .addCase(fetchGroupMembers.rejected, (state, action) => {
        state.membersLoading[action.meta.arg] = false
      })
      .addCase(addGroupMember.fulfilled, (state, action) => {
        const { groupId, member } = action.payload
        if (!state.membersById[groupId]) state.membersById[groupId] = []
        state.membersById[groupId].push(member)
      })
      .addCase(removeGroupMember.fulfilled, (state, action) => {
        const { groupId, userId } = action.payload
        if (state.membersById[groupId]) {
          state.membersById[groupId] = state.membersById[groupId].filter(
            (m) => m.user_id !== userId
          )
        }
      })
  },
})

export default groupsSlice.reducer
